import { useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Shield, ChevronDown, ChevronUp, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const CONSENT_KEY = "flow-clock-gdpr-consent";
const CONSENT_VERSION = "1.0";

interface ConsentScreenProps {
  onAccept: () => void;
}

export function ConsentScreen({ onAccept }: ConsentScreenProps) {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [acceptedData, setAcceptedData] = useState(false);
  const [acceptedRights, setAcceptedRights] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleAccept = useCallback(async () => {
    if (!acceptedData || !acceptedRights) return;
    setSaving(true);

    try {
      if (user) {
        await supabase.from("user_consents").upsert({
          user_id: user.id,
          consent_version: CONSENT_VERSION,
          data_collection: true,
          technical_cookies: true,
          performance_tracking: false,
        }, { onConflict: "user_id" });
      }

      localStorage.setItem(CONSENT_KEY, JSON.stringify({
        version: CONSENT_VERSION,
        date: new Date().toISOString(),
        accepted: true,
      }));

      onAccept();
    } catch {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({
        version: CONSENT_VERSION,
        date: new Date().toISOString(),
        accepted: true,
      }));
      onAccept();
    } finally {
      setSaving(false);
    }
  }, [acceptedData, acceptedRights, user, onAccept]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background">
      <div className="w-full max-w-md mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Welcome to Flow Clock</h1>
          <p className="text-sm text-muted-foreground mt-1.5">Your personal productivity companion</p>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto rounded-2xl bg-card border border-border p-5 space-y-5" style={{ WebkitOverflowScrolling: 'touch' }}>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Flow Clock helps you track and improve your focused work by measuring time spent on Deep Work vs Other activities.
          </p>

          {/* GDPR expandable section */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between p-3 rounded-xl bg-muted/50 border border-border/50 text-sm font-medium text-foreground"
          >
            <span className="flex items-center gap-2">
              📋 Your Data & Privacy
            </span>
            {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>

          {expanded && (
            <div className="space-y-4 text-sm animate-fade-in">
              <div>
                <h3 className="font-semibold text-foreground mb-2">What we collect:</h3>
                <ul className="space-y-1.5 text-muted-foreground">
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Time data (start/stop, duration, category)</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> User settings (goal, timeout, theme)</li>
                  <li className="flex items-start gap-2"><span className="text-primary">✓</span> Technical data (timezone, device type)</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">What we do NOT collect:</h3>
                <ul className="space-y-1.5 text-muted-foreground">
                  <li className="flex items-start gap-2"><span className="text-destructive">✗</span> GPS location or location data</li>
                  <li className="flex items-start gap-2"><span className="text-destructive">✗</span> Contacts, files, or other device data</li>
                  <li className="flex items-start gap-2"><span className="text-destructive">✗</span> Tracking cookies or advertising data</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">Security:</h3>
                <ul className="space-y-1.5 text-muted-foreground">
                  <li className="flex items-start gap-2">🔒 All data is stored encrypted</li>
                  <li className="flex items-start gap-2">🇪🇺 Data is stored within the EU</li>
                  <li className="flex items-start gap-2">🚫 No sharing with third parties</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">Your rights under GDPR:</h3>
                <ul className="space-y-1.5 text-muted-foreground">
                  <li className="flex items-start gap-2">📥 Export all your data</li>
                  <li className="flex items-start gap-2">🗑️ Delete your account and all data</li>
                  <li className="flex items-start gap-2">✏️ Correct inaccurate data</li>
                  <li className="flex items-start gap-2">⏸️ Pause data collection</li>
                </ul>
              </div>
            </div>
          )}

          {/* Checkboxes */}
          <div className="space-y-3 pt-2 border-t border-border/50">
            <label className="flex items-start gap-3 cursor-pointer group">
              <button
                onClick={() => setAcceptedData(!acceptedData)}
                className={cn(
                  "mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all",
                  acceptedData 
                    ? "bg-primary border-primary" 
                    : "border-muted-foreground/30 group-hover:border-muted-foreground/50"
                )}
              >
                {acceptedData && <Check className="w-3 h-3 text-primary-foreground" />}
              </button>
              <span className="text-xs text-muted-foreground leading-relaxed">
                I have read and accept the processing of my time data as described above
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer group">
              <button
                onClick={() => setAcceptedRights(!acceptedRights)}
                className={cn(
                  "mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all",
                  acceptedRights 
                    ? "bg-primary border-primary" 
                    : "border-muted-foreground/30 group-hover:border-muted-foreground/50"
                )}
              >
                {acceptedRights && <Check className="w-3 h-3 text-primary-foreground" />}
              </button>
              <span className="text-xs text-muted-foreground leading-relaxed">
                I understand that I can delete my data at any time via Settings
              </span>
            </label>
          </div>
        </div>

        {/* Bottom actions */}
        <div className="mt-4 space-y-3">
          <a
            href="/privacy"
            className="block text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            📋 Read full Privacy Policy
          </a>
          <button
            onClick={handleAccept}
            disabled={!acceptedData || !acceptedRights || saving}
            className={cn(
              "w-full py-3.5 rounded-2xl font-semibold text-sm transition-all duration-200",
              acceptedData && acceptedRights
                ? "bg-primary text-primary-foreground hover:opacity-90 active:scale-[0.98]"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            )}
          >
            {saving ? "Saving..." : "✓ I Understand and Accept"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function useGdprConsent() {
  const hasConsent = () => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (!stored) return false;
      const parsed = JSON.parse(stored);
      return parsed.accepted && parsed.version === CONSENT_VERSION;
    } catch {
      return false;
    }
  };

  return { hasConsent: hasConsent(), consentKey: CONSENT_KEY };
}
